import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { patchPwaInstalled } from "../../api/profile";
import { shouldGoToInstallPwa, getCurrentUser } from "../../utils/users";
import { logEvent } from "../../utils/events";
import Stepper from "../../components/Stepper";
import { getDeferredPrompt, setDeferredPrompt } from "../../utils/pwaPromptBus";
import { APP_CONFIG } from "../../utils/api";

type Props = {
  clientId?: string;
  nextRoute?: string;
};

export default function PwaInstallStep({ nextRoute = "/" }: Props) {
  const nav = useNavigate();
  const [cid, setCid] = useState<string>("");
  const [ready, setReady] = useState(false);

  // наш текущий хук
  const { installed, supported: hookSupported, showIosGuide, platform, inApp, canShowNow, promptInstall, markLater } =
    usePwaInstall({ cooldownDays: 7, maxPrompts: 3 });

  // читаем глобальный deferredPrompt, если он был пойман раньше
  const deferred = getDeferredPrompt();
  // если хук говорит "false", но deferred есть — считаем, что поддержка есть
  const supported = hookSupported || Boolean(deferred);

  const [busy, setBusy] = useState(false);
  const [showDesktopGuide, setShowDesktopGuide] = useState(false);

  // гард
  useEffect(() => {
    let alive = true;
    (async () => {
      const user = await getCurrentUser();
      if (!alive) return;
      if (!user) { nav("/courses/:id"); return; }
      setCid(user.client_id || "");
      setReady(true);
      const need = await shouldGoToInstallPwa(user.client_id || "");
      if (alive && !need && !showIosGuide) nav(nextRoute);
    })();
    return () => { alive = false; };
  }, [nav, nextRoute, showIosGuide]);

  // лог показа
  useEffect(() => {
    if (!ready) return;
    logEvent("pwa_install_shown", { client_id: cid, platform, inApp, supported });
  }, [ready, cid, platform, inApp, supported]);

  // уже установлен?
  useEffect(() => {
    if (!installed) return;
    (async () => {
      try {
        await patchPwaInstalled(true);
        logEvent("pwa_install_detected_standalone", { client_id: cid });
      } catch {}
      // после установки сбросим кеш
      setDeferredPrompt(null);
      nav(nextRoute);
    })();
  }, [installed, cid, nav, nextRoute]);

  // системный инсталл через наш хук ИЛИ через глобальный deferred
  const handleInstallClick = async () => {
    setBusy(true);
    try {
      logEvent("pwa_install_click", { client_id: cid });

      let accepted = false;

      if (deferred) {
        // вызовем системный диалог напрямую
        deferred.prompt();
        const choice = await deferred.userChoice;
        accepted = choice?.outcome === "accepted";
        // одноразовое событие — обнулим
        setDeferredPrompt(null);
      } else {
        // fallback на текущую реализацию хука
        accepted = await promptInstall();
      }

      if (accepted) {
        try {
          await patchPwaInstalled(true);
        } catch {}
        logEvent("pwa_install_accepted", { client_id: cid });
        nav(nextRoute);
      } else {
        markLater();
        try { await patchPwaInstalled(false); } catch {}
        logEvent("pwa_install_dismissed", { client_id: cid });
        nav(nextRoute);
      }
    } finally {
      setBusy(false);
    }
  };

  // единый обработчик: если реально нельзя показать системный диалог — открываем гайд
  const handleInstallClickOrGuide = async () => {
    if (supported) {
      await handleInstallClick();
    } else {
      setShowDesktopGuide(true);
      logEvent("pwa_install_show_desktop_guide", { client_id: cid, platform });
    }
  };

  const handleLater = async () => {
    markLater();
    logEvent("pwa_install_later", { client_id: cid });
    try { await patchPwaInstalled(false); } catch {}
    nav(nextRoute);
  };

  // тексты
  const copy = useMemo(() => {
    if (showIosGuide) {
      return {
        title: `Добавьте ${APP_CONFIG.brandName} на экран iPhone`,
        lead: "Открывайте курс в одно касание. Задания приходят вовремя, прогресс сохраняется офлайн.",
        bullets: [
          "Иконка на экране Домой",
          "Без лишних вкладок браузера",
          "Офлайн-доступ к прогрессу",
        ],
      };
    }
    if (platform === "android") {
      return {
        title: `Установите ${APP_CONFIG.brandName} как приложение`,
        lead: "Мгновенный доступ с главного экрана. Утренние задания — вовремя, даже офлайн.",
        bullets: [
          "Иконка на экране телефона",
          "Без браузера и лишних вкладок",
          "Сохранение прогресса офлайн",
        ],
      };
    }
    return {
      title: `Установите ${APP_CONFIG.brandName} на компьютер`,
      lead: "Открывайте курс как приложение: быстро, без вкладок и отвлечений. Прогресс синхронизируется.",
      bullets: [
        "Запуск из меню/десктопа",
        "Меньше отвлечений — больше фокуса",
        "Быстрый старт занятий утром",
      ],
    };
  }, [platform, showIosGuide]);

  const inAppWarning =
    inApp !== "none"
      ? `Сейчас вы в приложении ${inApp}. Установка PWA здесь не поддерживается. Откройте в стандартном браузере и вернитесь к этому шагу.`
      : null;

  if (!canShowNow && !showIosGuide) return null;

  return (
    <main className="min-h-screen w-full flex flex-col bg-white">
      <header className="w-full p-4">
        <Stepper
          current={3}
          steps={[
            { title: "Email" },
            { title: "Часовой пояс" },
            { title: "Установка" },
          ]}
        />
      </header>

      <section className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">
          <div className="rounded-2xl border border-gray-200 shadow-[0_6px_24px_rgba(0,0,0,0.06)] p-6 md:p-8 text-center">
            <h1 className="text-2xl md:text-3xl font-semibold mb-3">{copy.title}</h1>
            <p className="opacity-80 mb-6">{copy.lead}</p>

            <ul className="text-left mx-auto max-w-xl space-y-2 mb-6">
              {copy.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1">•</span>
                  <span className="opacity-90">{b}</span>
                </li>
              ))}
            </ul>

            {inAppWarning && (
              <div className="bg-yellow-50 text-yellow-900 rounded-xl p-4 mb-4 max-w-xl mx-auto text-left">
                <p className="font-medium mb-1">Откройте в стандартном браузере</p>
                <p className="text-sm opacity-90">{inAppWarning}</p>
              </div>
            )}

            {showIosGuide && (
              <div className="bg-gray-50 rounded-xl p-4 md:p-5 mb-6 mx-auto max-w-xl text-left">
                <h2 className="font-medium mb-2">Как установить на iPhone (Safari)</h2>
                <ol className="list-decimal pl-5 space-y-2 text-sm">
                  <li>Откройте сайт в Safari.</li>
                  <li>Нажмите «Поделиться» (квадрат со стрелкой внизу).</li>
                  <li>Выберите «На экран Домой» и подтвердите.</li>
                </ol>
                <p className="text-xs opacity-70 mt-3">В других браузерах iOS установка недоступна — используйте Safari.</p>
              </div>
            )}

            {!showIosGuide && inApp === "none" && platform !== "ios" && (
              <>
                <button
                  disabled={busy}
                  onClick={handleInstallClickOrGuide}
                  className="btn-cta inline-flex items-center justify-center px-7 py-3.5 rounded-2xl font-semibold
                             shadow-lg hover:shadow-xl
                             active:scale-[0.98] disabled:opacity-60
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
                >
                  Установить приложение
                </button>
                <div className="text-xs opacity-60 mt-2">Бесплатно • ~3 секунды</div>
              </>
            )}

            {showDesktopGuide && (
              <div className="bg-gray-50 rounded-xl p-4 md:p-5 mt-4 mx-auto max-w-xl text-left">
                <h2 className="font-medium mb-2">Как установить на компьютер</h2>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li><span className="font-medium">Chrome:</span> значок «Установить» в адресной строке или Меню ⋮ → <em>Установить {APP_CONFIG.brandName}</em>.</li>
                  <li><span className="font-medium">Edge:</span> кнопка «Установить» справа от адреса или Меню ⋯ → <em>Приложения → Установить этот сайт как приложение</em>.</li>
                  <li><span className="font-medium">Opera:</span> Меню O → <em>Установить сайт как приложение</em>.</li>
                </ul>
                <p className="text-xs opacity-70 mt-3">Если пункта нет — выйдите из приватного режима и обновите страницу.</p>
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={handleLater}
                disabled={busy}
                className="text-sm opacity-70 underline underline-offset-4 disabled:opacity-50"
              >
                Позже
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
